import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Edit, Delete, Close, Search } from "@mui/icons-material";
import AppTheme from "../components/shared-theme/AppTheme";
import { useCourses } from "../hooks/useCourses";
import Course from "../types/Course";

export default function DefinitionsCertificatons(props: {
  disableCustomTheme?: boolean;
}) {
  const { courses, loading, error, addCourse, updateCourse, deleteCourse } =
    useCourses();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course>({
    courseId: "",
    course: "",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<Course | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    if (error) {
      setSnackbarOpen(true);
    }
  }, [error]);

  const filteredSorted = React.useMemo(() => {
    const f = courses.filter((c) =>
      c.course.toLowerCase().includes(search.toLowerCase())
    );
    return f
      .slice()
      .sort((a, b) =>
        order === "asc"
          ? a.course.localeCompare(b.course, undefined, { sensitivity: "base" })
          : b.course.localeCompare(a.course, undefined, { sensitivity: "base" })
      );
  }, [courses, search, order]);

  const handleOpenDialog = (course?: Course) => {
    setEditingCourse(
      course || {
        courseId: "",
        course: "",
      }
    );
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCourse({ courseId: "", course: "" });
  };

  const handleSave = async () => {
    if (editingCourse.courseId) {
      await updateCourse(editingCourse.courseId, editingCourse.course);
    } else {
      await addCourse(editingCourse.course);
    }
    handleCloseDialog();
  };

  const confirmDelete = (course: Course) => {
    setCourseToDelete(course);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (courseToDelete) {
      await deleteCourse(courseToDelete.courseId);
      setDeleteDialogOpen(false);
      setCourseToDelete(null);
    }
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <Box p={4}>
        <Card>
          <CardContent>
            <Box sx={{ mb: 2.5 }}>
              {/* header row */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={{
                  pb: 2,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  component="h2"
                  variant="overline"
                  sx={{
                    color: "text.secondary",
                    letterSpacing: 0.8,
                    fontWeight: 700,
                  }}
                >
                  Trainings/Certifications
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog()}
                >
                  Add Certificate
                </Button>
              </Stack>

              {/* search row */}
              <TextField
                size="small"
                placeholder="Search courses…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mt: 2, width: "100%" }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearch("")}
                          aria-label="Clear"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  },
                }}
              />
            </Box>

            {loading ? (
              <Box
                sx={{
                  height: "350px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : courses.length === 0 ? (
              <Box
                sx={{
                  height: "350px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography align="center" color="textSecondary">
                  No courses available.
                </Typography>
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active
                        direction={order}
                        onClick={() =>
                          setOrder((o) => (o === "asc" ? "desc" : "asc"))
                        }
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSorted.map((course) => (
                    <TableRow key={course.courseId}>
                      <TableCell>{course.course}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          sx={{ marginRight: "5px" }}
                          onClick={() => handleOpenDialog(course)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => confirmDelete(course)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add / Edit Course Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          slotProps={{
            paper: {
              component: "form",
              onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
                event.preventDefault();
                handleCloseDialog();
              },
              sx: { backgroundImage: "none", width: "400px", maxWidth: "100%" },
            },
          }}
        >
          <DialogTitle>
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                letterSpacing: 0.8,
                fontWeight: 700,
              }}
            >
              {editingCourse.courseId ? "Edit Certificate" : "Add Certificate"}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <FormControl fullWidth>
              <FormLabel htmlFor="skillTypeId">Certificate</FormLabel>
              <TextField
                autoFocus
                fullWidth
                placeholder="Certificate name"
                value={editingCourse.course}
                onChange={(e) =>
                  setEditingCourse({
                    ...editingCourse,
                    course: e.target.value,
                  })
                }
              />
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Course dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete{" "}
              <strong>{courseToDelete?.course}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="warning"
            onClose={() => setSnackbarOpen(false)}
            sx={{ width: "100%" }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </AppTheme>
  );
}
